import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = { isLoading: false, data: {}, errors: [], myItems: [], myItemsLoading: false };

export const getShopItems = createAsyncThunk('shopItems/getShopItems', async (args, thunkApi) => {
  try {
    const { page = 1, size = 10, query = '', filter = JSON.stringify({}) } = args || {};
    const token = thunkApi.getState().auth.token;
    const { data } = await axios.get(
      `/api/get_shop_items?page=${page}&size=${size}&query=${query}&filter=${filter}`,
      token ? { headers: { Authorization: token } } : {}
    );
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const getMyShopItems = createAsyncThunk('shopItems/getMyShopItems', async (_, thunkApi) => {
  try {
    const { data } = await axios.get('/api/get_my_shop_items', {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const createShopItem = createAsyncThunk('shopItems/createShopItem', async (args, thunkApi) => {
  try {
    const { data } = await axios.post('/api/create_shop_item', args, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const updateShopItem = createAsyncThunk('shopItems/updateShopItem', async (args, thunkApi) => {
  try {
    const id = args.get ? args.get('_id') : args._id;
    const { data } = await axios.put(`/api/update_shop_item/${id}`, args, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const deleteShopItem = createAsyncThunk('shopItems/deleteShopItem', async (args, thunkApi) => {
  try {
    const { data } = await axios.delete(`/api/delete_shop_item/${args._id}`, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const approveShopItem = createAsyncThunk('shopItems/approveShopItem', async (args, thunkApi) => {
  try {
    const { data } = await axios.put(`/api/approve_shop_item/${args._id}`, {}, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const rejectShopItem = createAsyncThunk('shopItems/rejectShopItem', async (args, thunkApi) => {
  try {
    const { data } = await axios.put(`/api/reject_shop_item/${args._id}`, {}, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const purchaseShopItem = createAsyncThunk('shopItems/purchaseShopItem', async (args, thunkApi) => {
  try {
    const { data } = await axios.post(`/api/purchase_shop_item/${args._id}`, {}, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const cancelShopItem = createAsyncThunk('shopItems/cancelShopItem', async (args, thunkApi) => {
  try {
    const { data } = await axios.put(`/api/cancel_shop_item/${args._id}`, {}, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

const shopItemsSlice = createSlice({
  name: 'shopItems',
  initialState,
  extraReducers: {
    [getShopItems.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [getShopItems.fulfilled]: (state, action) => { state.isLoading = false; state.data = action.payload; },
    [getShopItems.rejected]: (state, action) => { state.isLoading = false; state.data = {}; state.errors = action.payload.errors; },

    [createShopItem.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [createShopItem.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.data.itemsCount = (state.data.itemsCount || 0) + 1;
      if ((state.data.data?.length || 0) < 10) state.data.data = [...(state.data.data || []), action.payload.data];
    },
    [createShopItem.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [updateShopItem.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [updateShopItem.fulfilled]: (state, action) => {
      state.isLoading = false;
      const idx = state.data.data?.findIndex(el => el._id === action.payload.data._id);
      if (idx !== undefined && idx >= 0) state.data.data[idx] = action.payload.data;
    },
    [updateShopItem.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [deleteShopItem.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [deleteShopItem.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.data.data = state.data.data?.filter(el => el._id !== action.payload.data._id);
      state.data.itemsCount = Math.max(0, (state.data.itemsCount || 1) - 1);
    },
    [deleteShopItem.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [approveShopItem.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [approveShopItem.fulfilled]: (state, action) => {
      state.isLoading = false;
      const idx = state.data.data?.findIndex(el => el._id === action.payload.data._id);
      if (idx !== undefined && idx >= 0) state.data.data[idx] = action.payload.data;
    },
    [approveShopItem.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [rejectShopItem.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [rejectShopItem.fulfilled]: (state, action) => {
      state.isLoading = false;
      const idx = state.data.data?.findIndex(el => el._id === action.payload.data._id);
      if (idx !== undefined && idx >= 0) state.data.data[idx] = action.payload.data;
    },
    [rejectShopItem.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [getMyShopItems.pending]: (state) => { state.myItemsLoading = true; state.myItems = []; },
    [getMyShopItems.fulfilled]: (state, action) => { state.myItemsLoading = false; state.myItems = action.payload.data; },
    [getMyShopItems.rejected]: (state) => { state.myItemsLoading = false; },

    [purchaseShopItem.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [purchaseShopItem.fulfilled]: (state, action) => {
      state.isLoading = false;
      const soldId = action.payload.data.item._id;
      state.data.data = state.data.data?.filter(el => el._id !== soldId);
    },
    [purchaseShopItem.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [cancelShopItem.pending]: (state) => { state.myItemsLoading = true; },
    [cancelShopItem.fulfilled]: (state, action) => {
      state.myItemsLoading = false;
      const idx = state.myItems?.findIndex(el => el._id === action.payload.data._id);
      if (idx !== undefined && idx >= 0) state.myItems[idx] = action.payload.data;
    },
    [cancelShopItem.rejected]: (state) => { state.myItemsLoading = false; },
  }
});

export default shopItemsSlice.reducer;
